import React, { useState, useEffect } from 'react';
import {
  X,
  CheckCircle,
  Clock,
  User,
  FileText,
  Calendar,
  Package,
  AlertCircle,
  Layers,
  UserPlus,
  CalendarPlus,
  Scale,
} from 'lucide-react';

const RecipeApprovalModal = ({ isOpen, onClose, recipeId }) => {
  const [approvalData, setApprovalData] = useState([]);
  const [recipeMetadata, setRecipeMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && recipeId) {
      fetchApprovalData();
    }
  }, [isOpen, recipeId]);

  const fetchApprovalData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/get_recipe_approval_by_recipe_id?recipe_id=${recipeId}`);
      const result = await response?.json();

      const approvals = result?.data?.data
        ? result?.data?.data
        : [
            {
              recipe_id: '1166',
              recipe_name: 'Demo',
              product_name: 'ATRCA',
              approval_status: 'approved',
              recipe_approval_id: 510,
              date_time: '2025-11-04 15:29:09.863931+00:00',
              done_by: '',
            },
            {
              recipe_id: '1166',
              recipe_name: 'Demo',
              product_name: 'ATRCA',
              approval_status: 'approved',
              recipe_approval_id: 511,
              date_time: '2025-11-04 15:29:09.865852+00:00',
              done_by: '',
            },
            {
              recipe_id: '1166',
              recipe_name: 'Demo',
              product_name: 'ATRCA',
              approval_status: 'approved',
              recipe_approval_id: 512,
              date_time: '2025-11-04 15:29:09.867211+00:00',
              done_by: '',
            },
          ];

      setApprovalData(approvals);

      // Extract recipe metadata from first approval record
      if (approvals.length > 0) {
        const firstApproval = approvals[0];
        setRecipeMetadata({
          recipe_id: firstApproval.recipe_id,
          recipe_name: firstApproval.recipe_name,
          product_name: firstApproval.product_name,
          // Additional metadata from API or defaults
          total_processes: result?.data?.total_processes || approvals.length,
          created_by: result?.data?.created_by || firstApproval.done_by || 'N/A',
          created_date: result?.data?.created_date || firstApproval.date_time,
          batch_size_min: result?.data?.batch_size_min || 100,
          batch_size_max: result?.data?.batch_size_max || 1000,
          batch_size_unit: result?.data?.batch_size_unit || 'kg',
        });
      }
    } catch (err) {
      setError('Failed to load approval data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  };

  const getStatusConfig = (status) => {
    const configs = {
      approved: {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle,
        iconColor: 'text-green-600',
        dotColor: 'bg-green-500',
      },
      pending: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock,
        iconColor: 'text-yellow-600',
        dotColor: 'bg-yellow-500',
      },
      rejected: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: AlertCircle,
        iconColor: 'text-red-600',
        dotColor: 'bg-red-500',
      },
    };
    return configs[status] || configs.pending;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Recipe Approval Workflow</h2>
            <p className="text-sm text-gray-500 mt-1">Recipe ID: {recipeId}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="mx-auto text-red-500 mb-2" size={48} />
                <p className="text-red-600">{error}</p>
              </div>
            </div>
          ) : approvalData?.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FileText className="mx-auto text-gray-400 mb-2" size={48} />
                <p className="text-gray-500">No approval records found</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Recipe Metadata Card */}
              {recipeMetadata && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-5 border-2 border-blue-200 shadow-md">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <FileText className="mr-2 text-blue-600" size={22} />
                    Recipe Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Recipe Name */}
                    <div className="bg-white rounded-lg p-3 border border-blue-100">
                      <div className="flex items-start space-x-3">
                        <FileText className="text-blue-600 mt-1" size={18} />
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Recipe Name</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {recipeMetadata.recipe_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Product Name */}
                    <div className="bg-white rounded-lg p-3 border border-purple-100">
                      <div className="flex items-start space-x-3">
                        <Package className="text-purple-600 mt-1" size={18} />
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Product Name</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {recipeMetadata.product_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Total Processes */}
                    <div className="bg-white rounded-lg p-3 border border-green-100">
                      <div className="flex items-start space-x-3">
                        <Layers className="text-green-600 mt-1" size={18} />
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Total Processes</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {recipeMetadata.total_processes}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Created By */}
                    <div className="bg-white rounded-lg p-3 border border-orange-100">
                      <div className="flex items-start space-x-3">
                        <UserPlus className="text-orange-600 mt-1" size={18} />
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Created By</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {recipeMetadata.created_by || (
                              <span className="text-gray-400 italic">Not specified</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Created Date */}
                    <div className="bg-white rounded-lg p-3 border border-cyan-100">
                      <div className="flex items-start space-x-3">
                        <CalendarPlus className="text-cyan-600 mt-1" size={18} />
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Created Date</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {recipeMetadata.created_date
                              ? formatDateTime(recipeMetadata.created_date).date
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Batch Size */}
                    <div className="bg-white rounded-lg p-3 border border-pink-100">
                      <div className="flex items-start space-x-3">
                        <Scale className="text-pink-600 mt-1" size={18} />
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Batch Size Range</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {recipeMetadata.batch_size_min} - {recipeMetadata.batch_size_max}{' '}
                            {recipeMetadata.batch_size_unit}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="relative">
                {/* Vertical Line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                {/* Timeline Items */}
                <div className="space-y-6">
                  {approvalData?.map((item, index) => {
                    const statusConfig = getStatusConfig(item?.approval_status);
                    const StatusIcon = statusConfig?.icon;
                    const { date, time } = formatDateTime(item?.date_time);

                    return (
                      <div
                        key={item.recipe_approval_id}
                        className="relative flex items-start space-x-4"
                      >
                        {/* Timeline Dot */}
                        <div className="relative z-10 flex-shrink-0">
                          <div
                            className={`w-12 h-12 rounded-full ${statusConfig.dotColor} flex items-center justify-center shadow-lg`}
                          >
                            <StatusIcon className="text-white" size={24} />
                          </div>
                        </div>

                        {/* Content Card */}
                        <div className="flex-1 bg-white rounded-lg border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                          <div className="p-4">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${statusConfig.color}`}
                              >
                                <StatusIcon
                                  className={`mr-1 ${statusConfig.iconColor}`}
                                  size={14}
                                />
                                {item.approval_status.toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500">
                                #{item.recipe_approval_id}
                              </span>
                            </div>

                            {/* Details */}
                            <div className="space-y-2">
                              <div className="flex items-center text-sm text-gray-700">
                                <Calendar className="text-gray-400 mr-2" size={16} />
                                <span className="font-medium">{date}</span>
                                <span className="mx-2 text-gray-400">•</span>
                                <span>{time}</span>
                              </div>

                              <div className="flex items-center text-sm text-gray-700">
                                <User className="text-gray-400 mr-2" size={16} />
                                <span>
                                  {item.done_by || (
                                    <span className="text-gray-400 italic">Not specified</span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Step Indicator */}
                          <div className="bg-gray-50 px-4 py-2 border-t border-gray-100">
                            <p className="text-xs text-gray-600">
                              Step {index + 1} of {approvalData?.length}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Total Approvals</span>
                  <span className="text-2xl font-bold text-gray-900">{approvalData?.length}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipeApprovalModal;
